import { type Query, type QueryClient } from "@tanstack/react-query";
import { ApiError } from "../api/client";

const MAX_READS = 4;
const DEFAULT_RETRY_COOLDOWN_MS = 30_000;

interface RunningRead {
  query: Query;
  owned: boolean;
}

export class RefreshScheduler {
  private readonly pending = new Map<string, Query>();
  private readonly repeated = new Set<string>();
  private readonly running = new Map<string, RunningRead>();
  private readonly waiters = new Set<(succeeded: boolean) => void>();
  private readonly cooldown = new Map<string, number>();
  private disposed = false;
  private failed = false;

  constructor(private readonly client: QueryClient) {}

  mark(predicate: (query: Query) => boolean): void {
    if (this.disposed) return;
    for (const query of this.client.getQueryCache().findAll({ predicate })) {
      query.invalidate();
      if (!query.isActive()) continue;
      if ((this.cooldown.get(query.queryHash) ?? 0) > Date.now()) continue;
      if (this.running.has(query.queryHash)) this.repeated.add(query.queryHash);
      else this.pending.set(query.queryHash, query);
    }
    this.drain();
  }

  resync(): Promise<boolean> {
    if (this.waiters.size > 0) {
      const { promise, resolve } = Promise.withResolvers<boolean>();
      this.waiters.add(resolve);
      return promise;
    }
    this.failed = false;
    this.mark(() => true);
    const cooled = this.client
      .getQueryCache()
      .findAll({ predicate: (query) => query.isActive() })
      .some((query) => (this.cooldown.get(query.queryHash) ?? 0) > Date.now());
    if (cooled) this.failed = true;
    if (this.pending.size === 0 && this.running.size === 0) {
      const succeeded = !this.failed;
      this.failed = false;
      return Promise.resolve(succeeded);
    }
    const { promise, resolve } = Promise.withResolvers<boolean>();
    this.waiters.add(resolve);
    return promise;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.pending.clear();
    this.repeated.clear();
    this.cooldown.clear();
    for (const { query, owned } of this.running.values()) {
      if (owned) void query.cancel({ silent: true });
    }
    this.running.clear();
    this.resolveWaiters();
  }

  private drain(): void {
    while (!this.disposed && this.running.size < MAX_READS && this.pending.size > 0) {
      const entry = this.pending.entries().next().value;
      if (!entry) break;
      const [hash, query] = entry;
      this.pending.delete(hash);
      const owned = query.state.fetchStatus === "idle";
      this.running.set(hash, { query, owned });
      void query
        .fetch()
        .catch(() => {
          this.failed = true;
        })
        .finally(() => this.finished(hash, query));
    }
    this.resolveWaiters();
  }

  private finished(hash: string, query: Query): void {
    if (!this.running.delete(hash) || this.disposed) return;
    const error = query.state.error;
    if (error instanceof ApiError && (error.status === 429 || error.status === 503)) {
      this.cooldown.set(hash, Date.now() + (error.retryAfterMs ?? DEFAULT_RETRY_COOLDOWN_MS));
    } else {
      this.cooldown.delete(hash);
    }
    if (
      this.repeated.delete(hash) &&
      query.isActive() &&
      (this.cooldown.get(hash) ?? 0) <= Date.now()
    ) {
      this.pending.set(hash, query);
    }
    this.drain();
  }

  private resolveWaiters(): void {
    if (this.pending.size > 0 || this.running.size > 0 || this.waiters.size === 0) return;
    const succeeded = !this.failed;
    this.failed = false;
    for (const resolve of this.waiters) resolve(succeeded);
    this.waiters.clear();
  }
}
